import type { SiteEntity, SiteLayer } from '../../types/site';
import { resolveSiteElevation } from '../resolveSiteElevation';
import { haversineMeters, isFiniteLatLng } from '../siteBoundaryGeometry';
import type {
  ReportClimateSummary,
  SitePreloadArcgisSummary,
  SitePreloadData,
} from './sitePreloadTypes';

export type {
  ReportClimateSummary,
  SitePreloadArcgisSummary,
  SitePreloadData,
} from './sitePreloadTypes';

function layerMinDistanceKm(
  layer: SiteLayer,
  center: { lat: number; lng: number } | undefined,
): number | undefined {
  // HOTFIX(lat-crash): `center` is `site.siteCentroid ?? site.location`, both
  // of which can be undefined on a partial / orphan stub doc. Without this
  // guard `haversineMeters(center, pt)` reads `center.lat` and throws
  // "Cannot read properties of undefined (reading 'lat')" the moment such a
  // site has any layer geometry. No usable centre → no distance.
  if (!isFiniteLatLng(center)) return undefined;
  const coords = layer.geometry?.coordinates ?? [];
  if (!coords.length) return undefined;
  let min = Number.POSITIVE_INFINITY;
  for (const pt of coords) {
    const d = haversineMeters(center, pt) / 1000;
    if (d < min) min = d;
  }
  return Number.isFinite(min) ? min : undefined;
}

function nearestLayerByCategory(
  layers: SiteLayer[],
  center: { lat: number; lng: number } | undefined,
  category: SiteLayer['category'],
): { layer: SiteLayer; distanceKm: number } | null {
  let best: { layer: SiteLayer; distanceKm: number } | null = null;
  for (const layer of layers) {
    if (layer.category !== category) continue;
    const distanceKm = layerMinDistanceKm(layer, center);
    if (distanceKm == null) continue;
    if (!best || distanceKm < best.distanceKm) best = { layer, distanceKm };
  }
  return best;
}

function nearestElectricalSubstationKm(
  layers: SiteLayer[],
  center: { lat: number; lng: number } | undefined,
): number | undefined {
  let nearest: number | undefined;
  for (const layer of layers) {
    if (layer.category !== 'infrastructure.electrical') continue;
    if (layer.geometry.type !== 'point') continue;
    const d = layerMinDistanceKm(layer, center);
    if (d == null) continue;
    if (nearest == null || d < nearest) nearest = d;
  }
  return nearest;
}

export function buildSitePreloadData(
  site: SiteEntity,
  reportClimate?: ReportClimateSummary,
): SitePreloadData {
  const sedClimate = site.sed?.climate?.data;
  const sedWaterStress = site.sed?.waterStress?.data?.stressLevel;
  const resolvedElevation = resolveSiteElevation(site, site.sed);

  const layers = site.siteLayers ?? [];
  // HOTFIX(lat-crash): both `siteCentroid` and `location` can be undefined on a
  // partial / orphan stub doc (`location` is typed required but is NOT runtime-
  // validated). Normalize to `undefined` when neither is a finite coordinate so
  // the distance helpers below short-circuit instead of dereferencing `.lat`.
  const center = isFiniteLatLng(site.siteCentroid)
    ? site.siteCentroid
    : isFiniteLatLng(site.location)
      ? site.location
      : undefined;
  const nearestPipeline = nearestLayerByCategory(layers, center, 'infrastructure.pipeline');
  const nearestTransmission = nearestLayerByCategory(layers, center, 'infrastructure.electrical');

  const arcgis: SitePreloadArcgisSummary = {
    pipelineLayerCount: layers.filter(l => l.category === 'infrastructure.pipeline').length,
    electricalLayerCount: layers.filter(l => l.category === 'infrastructure.electrical').length,
    environmentalConstraintLayerCount: layers.filter(l => l.category === 'constraint.environmental').length,
    nearestPipelineKm: nearestPipeline?.distanceKm,
    nearestTransmissionKm: nearestTransmission?.distanceKm,
    nearestSubstationKm: nearestElectricalSubstationKm(layers, center),
    nearestPipelineOperator: nearestPipeline?.layer.pipelineMeta?.providerName
      ? nearestPipeline.layer.pipelineMeta.providerName
      : nearestPipeline?.layer.name,
    nearestPipelineMassFlow: nearestPipeline?.layer.pipelineMeta?.massFlow,
    nearestPipelineAvailabilityPct: nearestPipeline?.layer.pipelineMeta?.availabilityPct,
    nearestPipelineGasComposition: nearestPipeline?.layer.pipelineMeta?.gasComposition,
  };

  const ambientTempC =
    reportClimate?.avgTemp
    ?? (typeof sedClimate?.avgTemp === 'number' ? sedClimate.avgTemp : undefined);
  const wetBulbTempC =
    reportClimate?.avgWetBulb
    ?? (typeof sedClimate?.avgWetBulb === 'number' ? sedClimate.avgWetBulb : undefined);
  const peakTempC =
    reportClimate?.peakTemp
    ?? (typeof sedClimate?.peakTemp === 'number' ? sedClimate.peakTemp : undefined);
  const humidityPct =
    reportClimate?.humidity
    ?? (typeof sedClimate?.humidity === 'number' ? sedClimate.humidity : undefined);
  const waterStress = reportClimate?.waterStress ?? sedWaterStress;

  return {
    ambientTempC,
    wetBulbTempC,
    peakTempC,
    humidityPct,
    waterStress,
    elevationM: resolvedElevation?.value.avg,
    slopePct: site.sed?.siteProfile?.data?.slopePercent,
    arcgis,
  };
}
